import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];
const WORD = "STUZIO. EDITFLOW";

export const Intro = ({ onDone }) => {
  const reduce = useReducedMotion();
  const startRef = useRef(Date.now());

  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const t = setTimeout(() => onDone(), reduce ? 1100 : 2350);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      clearTimeout(t);
    };
  }, [onDone, reduce]);

  const skip = () => {
    if (Date.now() - startRef.current >= 1000) onDone();
  };

  return (
    <motion.div
      data-testid="intro-overlay"
      onPointerDown={skip}
      className="fixed inset-0 z-[100] bg-ink flex flex-col items-center justify-center cursor-pointer select-none overflow-hidden"
      exit={
        reduce
          ? { opacity: 0, transition: { duration: 0.4, ease: "easeOut" } }
          : { y: "-100%", transition: { duration: 0.65, ease: [0.76, 0, 0.24, 1] } }
      }
      role="presentation"
    >
      <span
        className="absolute bottom-0 left-0 h-[3px] w-full bg-brand shadow-[0_0_28px_rgba(204,255,2,0.85)]"
        aria-hidden="true"
      />

      <motion.div
        className="flex flex-col items-center px-6"
        exit={reduce ? {} : { opacity: 0, transition: { duration: 0.18 } }}
      >
        <div className="relative mb-9">
          <motion.span
            className="absolute inset-0 -m-14 rounded-full bg-brand blur-3xl"
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={reduce ? { opacity: 0.18 } : { opacity: [0, 0.5, 0.22], scale: [0.6, 1.25, 1] }}
            transition={reduce ? { duration: 0.3 } : { delay: 0.95, duration: 0.8, ease: EASE }}
          />
          <div className="relative overflow-hidden" data-testid="intro-diamond">
            <motion.img
              src="/assets/logo-peak.png"
              alt="STUZIO. EDITFLOW pixel diamond mark"
              className="w-44 sm:w-56 h-auto drop-shadow-[0_0_26px_rgba(204,255,2,0.35)]"
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 26, scale: 0.94, filter: "blur(10px)" }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              transition={reduce ? { duration: 0.35 } : { delay: 0.15, duration: 1.05, ease: EASE }}
            />
            {!reduce && (
              <motion.span
                className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                aria-hidden="true"
                initial={{ x: "-160%" }}
                animate={{ x: "380%" }}
                transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
              />
            )}
          </div>
        </div>

        <p
          className="flex justify-center whitespace-nowrap font-sans font-extrabold tracking-[0.12em] text-[6.5vw] sm:text-4xl"
          aria-label="STUZIO. EDITFLOW"
          data-testid="intro-wordmark"
        >
          {WORD.split("").map((ch, i) => (
            <span key={i} className="overflow-hidden inline-block">
              <motion.span
                className={`inline-block ${ch === "." ? "text-brand" : i > 7 ? "font-light text-white/75" : "text-white"}`}
                initial={reduce ? { opacity: 0 } : { y: "115%" }}
                animate={reduce ? { opacity: 1 } : { y: "0%" }}
                transition={
                  reduce
                    ? { duration: 0.3, delay: 0.15 }
                    : { delay: 1.0 + i * 0.035, duration: 0.55, ease: EASE }
                }
              >
                {ch === " " ? "\u00A0" : ch}
              </motion.span>
            </span>
          ))}
        </p>

        <motion.span
          className="mt-5 h-px w-24 bg-brand"
          aria-hidden="true"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={reduce ? { duration: 0.3, delay: 0.2 } : { delay: 1.6, duration: 0.55, ease: EASE }}
        />

        <motion.p
          className="mt-5 font-mono text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-mute"
          data-testid="intro-tagline"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: reduce ? 0.3 : 1.75, duration: 0.5, ease: EASE }}
        >
          Smarter Marketing. Better Results.
        </motion.p>
      </motion.div>

      <motion.span
        className="absolute bottom-8 right-8 font-mono text-[9px] tracking-[0.3em] uppercase text-mute/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.4 }}
      >
        Tap to skip
      </motion.span>
    </motion.div>
  );
};
